import type { JsonSchemaNode } from "@nmblr-platform-flow/schema";
import { useEffect, useRef } from "react";
import type { TemplateVariable } from "../../lib/schema-paths.js";
import { FieldGroup } from "./FieldGroup.js";
import { SchemaField } from "./SchemaField.js";

interface SchemaFormProps {
	schema: JsonSchemaNode;
	value: Record<string, unknown>;
	onChange: (value: Record<string, unknown>) => void;
	templateVariables?: TemplateVariable[];
}

function parseCondition(condition: string): { field: string; value: string } | null {
	const eqIndex = condition.indexOf("=");
	if (eqIndex === -1) return null;
	return { field: condition.slice(0, eqIndex), value: condition.slice(eqIndex + 1) };
}

export function SchemaForm({ schema, value, onChange, templateVariables }: SchemaFormProps) {
	const properties = schema.properties;
	if (!properties || Object.keys(properties).length === 0) return null;

	const requiredFields = schema.required ?? [];

	// Evaluate conditions and partition fields
	const ungrouped: [string, JsonSchemaNode][] = [];
	const groups = new Map<string, [string, JsonSchemaNode][]>();
	const advanced: [string, JsonSchemaNode][] = [];
	const hiddenByCondition = new Set<string>();

	for (const [name, propSchema] of Object.entries(properties)) {
		const conditionStr = propSchema["x-forge-condition-field"];
		if (conditionStr) {
			const parsed = parseCondition(conditionStr);
			if (parsed && String(value[parsed.field]) !== parsed.value) {
				hiddenByCondition.add(name);
				continue;
			}
		}

		if (propSchema["x-forge-advanced"]) {
			advanced.push([name, propSchema]);
		} else if (propSchema["x-forge-group"]) {
			const groupName = propSchema["x-forge-group"];
			if (!groups.has(groupName)) groups.set(groupName, []);
			groups.get(groupName)?.push([name, propSchema]);
		} else {
			ungrouped.push([name, propSchema]);
		}
	}

	// Stable string key for the hidden set — avoids Set reference comparison in useEffect deps
	const hiddenKey = [...hiddenByCondition].sort().join(",");

	// Clear values for hidden fields via useEffect to avoid render-loop.
	// We intentionally omit value and onChange from deps to prevent an infinite loop:
	// including them would cause the effect to re-run whenever onChange fires.
	// We use a ref to snapshot value at effect time instead.
	const prevHiddenKeyRef = useRef("");
	const valueRef = useRef(value);
	valueRef.current = value;
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	useEffect(() => {
		const prev = new Set(prevHiddenKeyRef.current ? prevHiddenKeyRef.current.split(",") : []);
		prevHiddenKeyRef.current = hiddenKey;

		const newlyHidden = hiddenKey.split(",").filter((k) => k !== "" && !prev.has(k));
		if (newlyHidden.length === 0) return;

		const cleaned = { ...valueRef.current };
		let changed = false;
		for (const key of newlyHidden) {
			if (key in cleaned) {
				delete cleaned[key];
				changed = true;
			}
		}
		if (changed) onChangeRef.current(cleaned);
	}, [hiddenKey]);

	const renderField = (name: string, propSchema: JsonSchemaNode) => (
		<SchemaField
			key={name}
			name={name}
			schema={propSchema}
			value={value[name]}
			onChange={(v) => onChange({ ...value, [name]: v })}
			required={requiredFields.includes(name) || propSchema["x-forge-required"] === true}
			templateVariables={templateVariables}
		/>
	);

	return (
		<div>
			{ungrouped.map(([name, propSchema]) => renderField(name, propSchema))}

			{[...groups.entries()].map(([groupName, fields]) => (
				<FieldGroup key={groupName} name={groupName} defaultCollapsed={false}>
					{fields.map(([name, propSchema]) => renderField(name, propSchema))}
				</FieldGroup>
			))}

			{advanced.length > 0 && (
				<FieldGroup name="Advanced" defaultCollapsed={true} fieldCount={advanced.length}>
					{advanced.map(([name, propSchema]) => renderField(name, propSchema))}
				</FieldGroup>
			)}
		</div>
	);
}
